import React, { useState, useEffect, useCallback } from "react";
import { FiSearch, FiUsers, FiInbox, FiX, FiChevronDown, FiCheckCircle, FiClock as FiClockIcon, FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  limit
} from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/peerchats.css";
import PeerChatForm from "./PeerChatform";

const PeerChats = ({ onAcceptInquiry, peerStatus, onPeerStatusChange, showStatusDropdown, setShowStatusDropdown, onActiveChatsCountChange }) => {
  const statusOptions = [
    { label: "Ready", icon: FiCheckCircle, color: "#22c55e" },
    { label: "Away", icon: FiClockIcon, color: "#f59e0b" }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.label === status) || statusOptions[0];
  };
  const [inquiries, setInquiries] = useState([]); // Assigned/active/resolved/inactive from chats
  const [chats, setChats] = useState([]); // Standby chats
  const [escalatedChats, setEscalatedChats] = useState([]); // Store escalated chats from chats collection
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Reduced from 10 for testing pagination
  const [activeChatId, setActiveChatId] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [languageSpoken, setLanguageSpoken] = useState([]);
  const [tempNewLanguage, setTempNewLanguage] = useState("");
  const [showAwayModal, setShowAwayModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Default languages
  const defaultLanguages = ["english", "tagalog", "bisaya"];

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return "Just now";
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return `Just now`;
    if (diffMins < 60) return `${diffMins} mins`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  };

  // Map a chat document to a queue list item
  const mapChatToListItem = useCallback((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      chatId: docSnap.id,
      ticketNumber: data.ticketNumber,
      messagePreview: data.messagePreview || data.lastMessage,
      inquiryTopic: data.inquiryTopic,
      status: data.status,
      createdAt: data.createdAt,
      lastActivityAt: data.lastActivityAt,
      userId: data.userId,
      assignedTo: data.assignedTo || data.peerEducatorId,
      source: "chat",
      inquiryId: data.inquiryId,
      escalatedChat: data.escalatedChat || false,
      escalationStatus: data.escalationStatus || "pending-review"
    };
  }, []);

  // Handle chat action (enter chat or view resolved)
  const handleChatAction = async (chatId, currentStatus, assignedTo) => {
    try {
      const educatorId = auth.currentUser?.uid;

      if (!educatorId) {
        return;
      }

      if (assignedTo !== educatorId) {
        alert("You do not have permission to access this chat.");
        return;
      }

      if (currentStatus === "assigned" || currentStatus === "active" || currentStatus === "inactive") {
        if (currentStatus === "assigned") {
          await updateDoc(doc(db, "chats", chatId), {
            status: "active",
            updatedAt: serverTimestamp(),
            lastActivityAt: serverTimestamp()
          });
        }

        setActiveChatId(chatId);
        if (onAcceptInquiry) {
          onAcceptInquiry(chatId);
        }
      } else if (currentStatus === "resolved") {
        setActiveChatId(chatId);
        if (onAcceptInquiry) {
          onAcceptInquiry(chatId);
        }
      }

    } catch (error) {
      console.error("Failed to handle chat action:", error);
      alert("Failed to process chat. Please try again.");
    }
  };

  // Load educator's languages from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const loadLanguages = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setLanguageSpoken(userData.languageSpoken || userData.languages || []);
        }
      } catch (error) {
        console.error("Error loading educator's languages:", error);
      }
    };

    loadLanguages();
  }, []);

  // Single real-time listener for all educator's chats (consolidated from 6 separate listeners to 1)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      return;
    }

    const educatorId = user.uid;

    // Single consolidated query for all educator chats (all statuses in one query) - OPTIMIZED
    const qAllChats = query(
      collection(db, "chats"),
      where("peerEducatorId", "==", educatorId),
      where("status", "in", ["assigned", "active", "resolved", "inactive", "escalated", "standby"]),
      orderBy("createdAt", "desc"),
      limit(100) // Add limit
    );

    let mounted = true;

    const unsubscribeAllChats = onSnapshot(qAllChats,
      (snapshot) => {
        if (!mounted) return;
        
        const allChats = snapshot.docs.map(mapChatToListItem);
        
        // Separate chats by status
        const assignedActive = allChats.filter(chat => 
          chat.status === "assigned" || chat.status === "active"
        );
        const resolved = allChats.filter(chat => chat.status === "resolved");
        const inactive = allChats.filter(chat => chat.status === "inactive");
        const escalated = allChats.filter(chat => chat.status === "escalated");
        const standby = allChats.filter(chat => chat.status === "standby");
        
        // Sort helper function
        const sortByActivityTime = (a, b) => {
          const getTime = (item) => {
            if (item.lastActivityAt) return item.lastActivityAt.toDate?.()?.getTime() || item.lastActivityAt.seconds * 1000 || 0;
            return item.createdAt?.toDate?.()?.getTime() || item.createdAt?.seconds * 1000 || 0;
          };
          return getTime(b) - getTime(a);
        };

        // Merge assigned/active/resolved/inactive for inquiries
        const allEducatorChats = [...assignedActive, ...resolved, ...inactive].sort(sortByActivityTime);
        const sortedStandby = standby.sort(sortByActivityTime);

        // Batch all state updates together (single listener callback = single re-render)
        setInquiries(allEducatorChats);
        setEscalatedChats(escalated);
        setChats(sortedStandby);
        setIsRefreshing(false);
        setLoading(false);

        // Report active chat count to parent so it can gate logout
        if (onActiveChatsCountChange) {
          onActiveChatsCountChange(assignedActive.length);
        }
      },
      (error) => { console.error("Error fetching educator chats:", error); setIsRefreshing(false); }
    );

    return () => {
      mounted = false;
      unsubscribeAllChats();
    };
  }, [auth.currentUser?.uid, mapChatToListItem, refreshKey]); // refreshKey bump re-subscribes listener

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword, statusFilter]);

  // Combine inquiries and chats
  const allItems = [...inquiries, ...chats];
  
  // Filter items based on search and status
  const filteredItems = allItems.filter(item => {
    // Status filter
    if (statusFilter === "escalated") {
      // Don't filter here, we'll handle escalated separately
      return false;
    }
    if (statusFilter !== "all") {
      if (statusFilter === "assigned" && item.status !== "assigned" && item.status !== "active") {
        return false;
      } else if (statusFilter !== "assigned" && item.status !== statusFilter) {
        return false;
      }
    }
    
    // Search filter
    if (!searchKeyword) return true;
    const keyword = searchKeyword.toLowerCase();
    return (
      item.ticketNumber?.toLowerCase().includes(keyword) ||
      item.messagePreview?.toLowerCase().includes(keyword)
    );
  });

  // When filtering by escalated, use only escalated chats from chats collection
  let displayItems = filteredItems;
  if (statusFilter === "escalated") {
    displayItems = escalatedChats;
  }

  // Calculate pagination values
  const totalPages = Math.ceil(displayItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = displayItems.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle back to queue navigation
  const handleBackToQueue = () => {
    setActiveChatId(null);
  };

  // Handle language checkbox toggle
  const handleLanguageToggle = (lang) => {
    setLanguageSpoken(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  // Handle adding a custom language
  const handleAddCustomLanguage = () => {
    const trimmed = tempNewLanguage.trim();
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) {
      alert("Language should only contain letters");
      return;
    }
    if (trimmed.length > 15) {
      alert("Language cannot exceed 15 characters");
      return;
    }
    const lowerLang = trimmed.toLowerCase();
    if (!languageSpoken.includes(lowerLang) && !defaultLanguages.includes(lowerLang)) {
      setLanguageSpoken(prev => [...prev, lowerLang]);
    }
    setTempNewLanguage("");
  };

  // Handle Apply button in modal
  const handleApplyLanguages = async () => {
    const user = auth.currentUser;
    if (!user) return;

    if (languageSpoken.length === 0) {
      alert("Please select at least one language");
      return;
    }

    try {
      await updateDoc(doc(db, "users", user.uid), {
        languageSpoken: languageSpoken
      });
      setShowLanguageModal(false);
    } catch (error) {
      console.error("Error saving languages:", error);
      alert("Failed to save languages. Please try again.");
    }
  };

  // Handle status change with active chats check
  const handleStatusChange = (newStatus) => {
    if (newStatus === "Away") {
      const activeChatsCount = inquiries.filter(i => i.status === "assigned" || i.status === "active").length;
      if (activeChatsCount > 0) {
        setShowAwayModal(true);
        return;
      }
    }
    onPeerStatusChange(newStatus);
  };

  return (
    <div className="ma-main review-appointments-panel">
      {activeChatId ? (
        // Show PeerChatForm when chat is active
        <PeerChatForm chatId={activeChatId} onBackToQueue={handleBackToQueue} />
      ) : (
        // Show inquiry queue when no active chat
        <>
          {/* Header */}
          <div className="content-library-header-custom" style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            borderRadius: '20px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Peer Educator - Left Aligned */}
            <div style={{
              position: 'absolute',
              left: '32px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}>
              <div style={{
                padding: '16px 32px',
                borderRadius: '50px',
                background: 'rgba(147, 197, 253, 0.2)',
                border: '1px solid rgba(147, 197, 253, 0.3)'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: '0.02em',
                  textShadow: 'none'
                }}>
                  Peer Educator
                </h2>
              </div>
            </div>

            {/* Title and Subtitle */}
            <div style={{ textAlign: 'right' }}>
              <h2 style={{
                margin: 0,
                fontSize: '36px',
                fontWeight: '800',
                color: '#ffffff !important',
                background: 'none !important',
                WebkitBackgroundClip: 'unset !important',
                WebkitTextFillColor: '#ffffff !important',
                backgroundClip: 'unset !important',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                Chat Queue
              </h2>
              <p style={{
                margin: '12px 0 0 0',
                fontSize: '18px',
                color: '#ffffff',
                fontWeight: '500',
                textShadow: '0 1px 4px rgba(0,0,0,0.15)'
              }}>
                Manage peer educator inquiries and conversations
              </p>
            </div>

            {/* CSS Animations */}
            <style>{`
              .content-library-header-custom h2 {
                color: #ffffff !important;
                background: none !important;
                -webkit-background-clip: unset !important;
                -webkit-text-fill-color: #ffffff !important;
                background-clip: unset !important;
              }
              .content-library-header-custom p {
                color: #ffffff !important;
              }
            `}</style>
          </div>

          <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
            {/* Search bar */}
            <div style={{ position: 'relative', flex: 1 }}>
              <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                type="text"
                placeholder="Search inquiries..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="admin-aura-search"
                style={{ paddingLeft: 36, width: '100%' }}
              />
            </div>

            {/* Languages wrapper */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', minHeight: '40px' }}>
              {languageSpoken.map((lang, index) => {
                let label;
                if (lang === "english") label = "English";
                else if (lang === "tagalog") label = "Tagalog";
                else if (lang === "bisaya") label = "Bisaya";
                else label = lang;
                return (
                  <span key={index} className="language-pill">
                    {label}
                  </span>
                );
              })}
              <button
                className="add-languages-btn"
                onClick={() => setShowLanguageModal(true)}
              >
                Add Languages
              </button>
            </div>

            {/* Active chats count */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', border: '1px solid #3b82f6', minHeight: '40px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#ffffff' }}>Active:</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>{inquiries.filter(i => i.status === "assigned" || i.status === "active").length}</span>
            </div>

            {/* Refresh button */}
            <button
              className="admin-primary-gradient-btn"
              onClick={() => { setIsRefreshing(true); setRefreshKey(k => k + 1); }}
              disabled={isRefreshing}
              style={{ opacity: isRefreshing ? 0.6 : 1, cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
            >
              <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>

            {/* Peer status dropdown */}
            <div className="pe-status-dropdown">
              <button
                className="pe-status-toggle-btn"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              >
                {(() => {
                  const config = getStatusConfig(peerStatus);
                  const Icon = config.icon;
                  return (
                    <>
                      <Icon className="pe-status-icon" style={{ color: config.color }} />
                      <span className="pe-status-label">{peerStatus}</span>
                      <FiChevronDown className={`pe-status-chevron ${showStatusDropdown ? 'open' : ''}`} />
                    </>
                  );
                })()}
              </button>
              {showStatusDropdown && (
                <div className="pe-status-dropdown-menu">
                  {statusOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.label}
                        className={`pe-status-option ${peerStatus === option.label ? 'active' : ''}`}
                        onClick={() => handleStatusChange(option.label)}
                      >
                        <Icon className="pe-status-option-icon" style={{ color: option.color }} />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Status Tabs + Pagination Row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", marginTop: "20px", padding: "0 28px" }}>
            <div className="floating-index-tabs ma-status-tabs" aria-label="Filter inquiries by status" style={{ display: "flex", gap: "8px" }}>
              <button 
                className={`floating-tab ${statusFilter === "all" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("all")}
              >
                All
                <span className="tab-count">{allItems.length}</span>
              </button>
              <button 
                className={`floating-tab ${statusFilter === "assigned" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("assigned")}
              >
                Assigned
                <span className="tab-count">{inquiries.filter(i => i.status === "assigned" || i.status === "active").length}</span>
              </button>
              <button 
                className={`floating-tab ${statusFilter === "standby" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("standby")}
              >
                Standby
                <span className="tab-count">{chats.length}</span>
              </button>
              <button 
                className={`floating-tab ${statusFilter === "resolved" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("resolved")}
              >
                Resolved
                <span className="tab-count">{inquiries.filter(i => i.status === "resolved").length}</span>
              </button>
              <button 
                className={`floating-tab ${statusFilter === "inactive" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("inactive")}
              >
                Inactive
                <span className="tab-count">{inquiries.filter(i => i.status === "inactive").length}</span>
              </button>
              <button 
                className={`floating-tab ${statusFilter === "escalated" ? "active floating-tab-active" : ""}`}
                onClick={() => setStatusFilter("escalated")}
              >
                Escalated
                <span className="tab-count">{escalatedChats.length}</span>
              </button>
            </div>

            <div style={{ 
              display: "flex", 
              gap: "16px", 
              alignItems: "center" 
            }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {displayItems.length > 0 ? (
                  `${startIndex + 1}-${Math.min(endIndex, displayItems.length)} of ${displayItems.length}`
                ) : (
                  '0 of 0'
                )}
              </div>

              {/* Pagination Arrows */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  ←
                </button>
                
                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{ 
                    backgroundColor: '#1e3a8a', 
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          <div className="peer-chats-content">
            {loading ? (
              <div className="inquiries-table">
                <div className="table-header">
                  <div>Ticket Number</div>
                  <div>Topic</div>
                  <div>Status</div>
                  {statusFilter === "escalated" && <div>Escalation Status</div>}
                  {statusFilter !== "escalated" && <div>Last Active</div>}
                  <div>Action</div>
                </div>
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="table-row admin-aura-skeleton-row">
                    <div><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></div>
                    <div><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></div>
                    <div><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '30%' }} /></div>
                    {statusFilter === "escalated" && <div><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40%' }} /></div>}
                    {statusFilter !== "escalated" && <div><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></div>}
                    <div><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></div>
                  </div>
                ))}
              </div>
            ) : displayItems.length === 0 ? (
              <div className="empty-state">
                <FiInbox className="empty-icon" />
                <h3>No inquiries found</h3>
                <p>No inquiries match your search criteria.</p>
              </div>
            ) : (
              <div className="inquiries-table">
                <div className="table-header">
                  <div>Ticket Number</div>
                  <div>Topic</div>
                  <div>Status</div>
                  {statusFilter === "escalated" && <div>Escalation Status</div>}
                  {statusFilter !== "escalated" && <div>Last Active</div>}
                  <div>Action</div>
                </div>
                {paginatedItems.map((inquiry) => (
                  <div key={inquiry.id} className="table-row">
                    <div className="ticket-number">{inquiry.ticketNumber || "N/A"}</div>
                    <div className="message-preview">
                      {statusFilter === "escalated" ? (
                        <div style={{ fontWeight: "bold" }}>{inquiry.inquiryTopic || "N/A"}</div>
                      ) : (
                        <>
                          {inquiry.inquiryTopic && (
                            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>{inquiry.inquiryTopic}</div>
                          )}
                          <div>{inquiry.messagePreview}</div>
                        </>
                      )}
                    </div>
                    <div className={`status ${inquiry.escalatedChat || inquiry.status === 'escalated' ? 'escalated' : inquiry.status === 'active' ? 'assigned' : inquiry.status}`}>
                      <span className="pill">
                        {inquiry.escalatedChat || inquiry.status === 'escalated' ? "Escalated" : 
                         inquiry.status === "active" ? "Assigned" : 
                         inquiry.status === "assigned" ? "Assigned" : 
                         inquiry.status === "claimed" ? "Claimed" : 
                         inquiry.status === "inactive" ? "Inactive" : 
                         inquiry.status === "standby" ? "Standby" : 
                         inquiry.status === "archived" ? "Archived" : "Resolved"}
                      </span>
                    </div>
                    {statusFilter === "escalated" && (
                      <div className={`escalation-status ${inquiry.escalationStatus}`}>
                        <span className="pill">
                          {inquiry.escalationStatus === 'under-assessment' ? 'Under Assessment' :
                          inquiry.escalationStatus === 'pending-review' ? 'Pending Review' :
                           inquiry.escalationStatus === 'resolved' ? 'Resolved' : 'Escalated'}
                        </span>
                      </div>
                    )}
                    {statusFilter !== "escalated" && (
                      <div className="created-time">{formatTimestamp(inquiry.lastActivityAt || inquiry.createdAt)}</div>
                    )}
                    <div className="action">
                      {inquiry.escalatedChat || inquiry.status === 'escalated' ? (
                        <button
                          className="pc-btn-action pc-btn-view"
                          onClick={() => {
                            if (inquiry.chatId) {
                              setActiveChatId(inquiry.chatId);
                              if (onAcceptInquiry) {
                                onAcceptInquiry(inquiry.chatId);
                              }
                            }
                          }}
                          title="View escalated chat (read-only)"
                        >
                          View Chat
                        </button>
                      ) : inquiry.source === "chat" && inquiry.chatId ? (
                        <button
                          className="pc-btn-action pc-btn-continue"
                          onClick={() => {
                            setActiveChatId(inquiry.chatId);
                            if (onAcceptInquiry) {
                              onAcceptInquiry(inquiry.chatId);
                            }
                          }}
                          title="Enter chat"
                        >
                          Enter Chat
                        </button>
                      ) : inquiry.status === "resolved" || inquiry.status === "archived" ? (
                        <button
                          className="pc-btn-action pc-btn-view"
                          onClick={() => handleChatAction(inquiry.chatId || inquiry.id, inquiry.status, inquiry.assignedTo)}
                          title="View resolved chat"
                        >
                          View Chat
                        </button>
                      ) : (
                        <button
                          className="pc-btn-action pc-btn-continue"
                          onClick={() => handleChatAction(inquiry.chatId || inquiry.id, inquiry.status, inquiry.assignedTo)}
                          title="Enter chat"
                        >
                          Enter Chat
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Language Selection Modal - Refined with System Branding */}
      {showLanguageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '550px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.3)'
          }}>
            {/* Header with System Branding */}
            <div style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#fff', 
                fontSize: '18px', 
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FiUsers style={{ fontSize: '20px' }} /> Which languages do you speak?
              </h3>
              <button
                type="button"
                onClick={() => setShowLanguageModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '10px',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <FiX style={{ fontSize: '18px' }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '28px' }}>
              {/* Language Options */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '10px', 
                marginBottom: '24px'
              }}>
                {/* Default languages */}
                {defaultLanguages.map((lang) => {
                  let label;
                  if (lang === "english") label = "English";
                  else if (lang === "tagalog") label = "Tagalog";
                  else if (lang === "bisaya") label = "Bisaya";
                  
                  return (
                    <button
                      key={lang}
                      onClick={() => handleLanguageToggle(lang)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '999px',
                        border: languageSpoken.includes(lang) 
                          ? '2px solid #3f62b5' 
                          : '1px solid #e2e8f0',
                        background: languageSpoken.includes(lang) 
                          ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                          : '#fff',
                        color: languageSpoken.includes(lang) ? '#fff' : '#475569',
                        fontSize: '14px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: languageSpoken.includes(lang) 
                          ? '0 4px 12px rgba(63, 98, 181, 0.3)' 
                          : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!languageSpoken.includes(lang)) {
                          e.target.style.borderColor = '#3f62b5';
                          e.target.style.color = '#3f62b5';
                          e.target.style.background = '#eff6ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!languageSpoken.includes(lang)) {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.color = '#475569';
                          e.target.style.background = '#fff';
                        }
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {/* Custom languages */}
                {languageSpoken.filter(lang => !defaultLanguages.includes(lang)).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleLanguageToggle(lang)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '999px',
                      border: languageSpoken.includes(lang) 
                        ? '2px solid #3f62b5' 
                        : '1px solid #e2e8f0',
                      background: languageSpoken.includes(lang) 
                        ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                        : '#fff',
                      color: languageSpoken.includes(lang) ? '#fff' : '#475569',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: languageSpoken.includes(lang) 
                        ? '0 4px 12px rgba(63, 98, 181, 0.3)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!languageSpoken.includes(lang)) {
                        e.target.style.borderColor = '#3f62b5';
                        e.target.style.color = '#3f62b5';
                        e.target.style.background = '#eff6ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!languageSpoken.includes(lang)) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.color = '#475569';
                        e.target.style.background = '#fff';
                      }
                    }}
                  >
                    {lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </button>
                ))}
              </div>

              {/* Add custom language */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '10px'
                }}>
                  <input
                    type="text"
                    placeholder="Add a language..."
                    value={tempNewLanguage}
                    onChange={(e) => setTempNewLanguage(e.target.value)}
                    maxLength={15}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCustomLanguage()}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      fontSize: '15px',
                      background: '#f8fafc',
                      color: '#1e293b',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3f62b5';
                      e.target.style.boxShadow = '0 0 0 3px rgba(63, 98, 181, 0.1)';
                      e.target.style.background = '#fff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = '#f8fafc';
                    }}
                  />
                  <button
                    onClick={handleAddCustomLanguage}
                    disabled={!tempNewLanguage.trim()}
                    style={{
                      padding: '12px 24px',
                      background: tempNewLanguage.trim() 
                        ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                        : '#cbd5e1',
                      color: tempNewLanguage.trim() ? '#fff' : '#94a3b8',
                      fontSize: '14px',
                      fontWeight: 700,
                      borderRadius: '10px',
                      border: 'none',
                      cursor: tempNewLanguage.trim() ? 'pointer' : 'not-allowed',
                      boxShadow: tempNewLanguage.trim() ? '0 4px 12px rgba(63, 98, 181, 0.3)' : 'none',
                      transition: 'all 0.2s'
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px'
              }}>
                <button 
                  onClick={() => setShowLanguageModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    background: '#f1f5f9',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: 700,
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e2e8f0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleApplyLanguages}
                  disabled={languageSpoken.length === 0}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    background: languageSpoken.length > 0 
                      ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' 
                      : '#cbd5e1',
                    color: languageSpoken.length > 0 ? '#fff' : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: 700,
                    borderRadius: '10px',
                    border: 'none',
                    cursor: languageSpoken.length > 0 ? 'pointer' : 'not-allowed',
                    boxShadow: languageSpoken.length > 0 ? '0 4px 12px rgba(63, 98, 181, 0.3)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Away Status Warning Modal */}
      {showAwayModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.3)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FiAlertTriangle style={{ fontSize: '20px' }} /> Cannot Go Away
              </h3>
              <button
                type="button"
                onClick={() => setShowAwayModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '10px',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <FiX style={{ fontSize: '18px' }} />
              </button>
            </div>

            <div style={{ padding: '28px' }}>
              <p style={{
                margin: '0 0 24px 0',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                You cannot switch to "Away" status while you have active chats. Please complete or resolve your active chats before changing your status.
              </p>

              <button
                onClick={() => setShowAwayModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(30, 58, 138, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(30, 58, 138, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px rgba(30, 58, 138, 0.2)';
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeerChats;
